import { useState, useMemo, useEffect } from "react";
import {
  Card,
  Button,
  Input,
  Select,
  Modal,
  Form,
  Switch,
  message,
  Drawer,
  Descriptions,
  Tag,
  Space,
  Divider,
  InputNumber,
} from "antd";
import { Plus, Search, Settings2, ArrowLeft } from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useCustomFieldDefinitionList,
  useCustomFieldDefinitionCreate,
  useCustomFieldDefinitionUpdate,
  useCustomFieldDefinitionDelete,
} from "../../../api-hooks/customFields";
import { useCompanyList } from "../../../api-hooks/companies";
import {
  TCustomFieldDefinition,
  TCustomFieldType,
} from "../../../types/customFields";
import CustomFieldsTable from "./CustomFieldsTable";
import dayjs from "dayjs";

const FIELD_TYPE_OPTIONS: { value: TCustomFieldType; label: string }[] = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "boolean", label: "Boolean (Yes/No)" },
  { value: "select", label: "Select (single)" },
  { value: "multiselect", label: "Multi-select" },
  { value: "file", label: "File upload" },
];

const ENTITY_TYPE_OPTIONS = [
  { value: "job", label: "Job" },
  { value: "quote", label: "Quote" },
  { value: "invoice", label: "Invoice" },
  { value: "client", label: "Client" },
  { value: "user", label: "User" },
  { value: "company", label: "Company" },
  { value: "asset", label: "Asset" },
  { value: "project", label: "Project" },
];

export default function CustomFields() {
  const { companyId: urlCid } = useParams<{ companyId?: string }>();
  const lockedCompanyId = urlCid ? Number(urlCid) : null;
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [messageApi, ctx] = message.useMessage();

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [entityFilter, setEntity] = useState<string>("");
  const [activeFilter, setActive] = useState<string>("");
  const [companyFilter, setCompanyFilter] = useState<number | null>(lockedCompanyId);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<TCustomFieldDefinition | null>(null);
  const [detail, setDetail] = useState<TCustomFieldDefinition | null>(null);
  const [optionInput, setOptionInput] = useState("");
  const [options, setOptions] = useState<string[]>([]);
  const [fieldType, setFieldType] = useState<TCustomFieldType>("text");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  const queryData = useMemo(() => {
    const q: any = { page, limit };
    if (entityFilter) q.entity_type = entityFilter;
    if (activeFilter !== "") q.is_active = activeFilter === "active";
    if (companyFilter) q.company_id = companyFilter;
    if (debouncedSearch.trim()) q.search = debouncedSearch.trim();
    return q;
  }, [page, limit, entityFilter, activeFilter, debouncedSearch, companyFilter]);

  const {
    data: result,
    isLoading,
    refetch,
  } = useCustomFieldDefinitionList(queryData);

  useEffect(() => {
    refetch();
  }, [queryData, refetch]);

  const definitions = result?.data ?? [];
  const total = result?.total ?? 0;

  const { data: companies = [], isLoading: companiesLoading } = useCompanyList({
    limit: 500,
  });

  // ─── mutations ───────────────────────────────────────────────────────────
  const { mutate: createDef, isPending: creating } =
    useCustomFieldDefinitionCreate({
      onSuccess: () => {
        messageApi.success("Custom field created.");
        resetForm();
      },
      onError: (e: any) => messageApi.error(e?.message || "Failed to create."),
    });

  const { mutate: updateDef, isPending: updating } =
    useCustomFieldDefinitionUpdate({
      onSuccess: () => {
        messageApi.success("Custom field updated.");
        resetForm();
      },
      onError: (e: any) => messageApi.error(e?.message || "Failed to update."),
    });

  const { mutate: deleteDef } = useCustomFieldDefinitionDelete({
    onSuccess: () => messageApi.success("Custom field deleted."),
    onError: () => messageApi.error("Failed to delete."),
  });

  // ─── helpers ─────────────────────────────────────────────────────────────
  function resetForm() {
    setShowForm(false);
    setEditing(null);
    setOptions([]);
    setOptionInput("");
    setFieldType("text");
    form.resetFields();
  }

  function openCreate() {
    resetForm();
    setShowForm(true);
  }

  function openEdit(row: TCustomFieldDefinition) {
    setEditing(row);
    const opts: string[] = row.options
      ? typeof row.options === "string"
        ? JSON.parse(row.options)
        : row.options
      : [];
    setOptions(opts);
    setFieldType(row.field_type);
    form.setFieldsValue({
      company_id: row.company_id,
      entity_type: row.entity_type,
      field_key: row.field_key,
      field_label: row.field_label,
      field_type: row.field_type,
      is_required: !!row.is_required,
      sort_order: row.sort_order,
      is_active: !!row.is_active,
    });
    setShowForm(true);
  }

  function handleAddOption() {
    const val = optionInput.trim();
    if (!val || options.includes(val)) return;
    setOptions((prev) => [...prev, val]);
    setOptionInput("");
  }

  function handleRemoveOption(opt: string) {
    setOptions((prev) => prev.filter((o) => o !== opt));
  }

  function handleSubmit(values: any) {
    const needsOptions = ["select", "multiselect"].includes(values.field_type);
    const finalOptions = [...options];
    if (needsOptions && optionInput.trim() && !finalOptions.includes(optionInput.trim())) {
      finalOptions.push(optionInput.trim());
      setOptions(finalOptions);
      setOptionInput("");
    }
    const payload = {
      ...values,
      options: needsOptions ? (finalOptions.length > 0 ? finalOptions : null) : null,
    };
    if (editing) {
      updateDef({ id: editing.id, data: payload });
    } else {
      createDef(payload);
    }
  }

  const needsOptions = ["select", "multiselect"].includes(fieldType);

  return (
    <div>
      {ctx}

      {/* ── Header ── */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          {lockedCompanyId && <Button icon={<ArrowLeft size={16} />} onClick={() => navigate(-1)} />}
          <div>
            <h2 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
              <Settings2 size={20} className="text-violet-600" />
              Custom Fields {lockedCompanyId ? `— Company #${lockedCompanyId}` : ""}
            </h2>
            <p className="text-slate-500 text-sm mt-0.5">
              {total} definition{total !== 1 ? "s" : ""}{lockedCompanyId ? " for this company" : " across all entity types"}
            </p>
          </div>
        </div>
        <Space>
          <Button
            type="primary"
            icon={<Plus size={14} />}
            onClick={openCreate}
            style={{ background: "#4c1d95", borderColor: "#4c1d95" }}
          >
            New Field
          </Button>
        </Space>
      </div>

      {/* ── Filters ── */}
      <Card className="rounded-2xl border border-slate-200 shadow-sm mb-4">
        <div className="flex flex-wrap gap-3">
          <Input
            prefix={<Search size={14} className="text-slate-400" />}
            placeholder="Search by label or key…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="rounded-lg"
            style={{ maxWidth: 280 }}
            allowClear
          />
          {!lockedCompanyId && (
            <Select
              showSearch
              placeholder="Company"
              value={companyFilter || undefined}
              onChange={(v) => {
                setCompanyFilter(v ?? null);
                setPage(1);
              }}
              allowClear
              optionFilterProp="label"
              style={{ width: 170 }}
              options={companies.map((c) => ({
                value: c.id,
                label: c.name,
              }))}
            />
          )}
          <Select
            placeholder="Entity type"
            value={entityFilter || undefined}
            onChange={(v) => {
              setEntity(v || "");
              setPage(1);
            }}
            allowClear
            style={{ width: 170 }}
            options={ENTITY_TYPE_OPTIONS}
          />
          <Select
            placeholder="Status"
            value={activeFilter || undefined}
            onChange={(v) => {
              setActive(v ?? "");
              setPage(1);
            }}
            allowClear
            style={{ width: 130 }}
            options={[
              { value: "active", label: "Active" },
              { value: "inactive", label: "Inactive" },
            ]}
          />
        </div>
      </Card>

      {/* ── Table ── */}
      <Card
        className="rounded-2xl border border-slate-200 shadow-sm"
        styles={{ body: { padding: 0 } }}
      >
        <CustomFieldsTable
          data={definitions}
          loading={isLoading}
          total={total}
          qData={{ page, limit }}
          companies={companies}
          onChange={(q) => {
            setPage(q.page ?? 1);
            setLimit(q.limit ?? 20);
          }}
          onEdit={openEdit}
          onDelete={(id) => deleteDef(id)}
        />
      </Card>

      {/* ── Create / Edit Modal ── */}
      <Modal
        open={showForm}
        onCancel={resetForm}
        title={
          <span className="text-slate-800 font-semibold">
            {editing ? "Edit Custom Field" : "New Custom Field"}
          </span>
        }
        footer={null}
        width={620}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            is_required: false,
            is_active: true,
            sort_order: 0,
            field_type: "text",
          }}
        >
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item
              name="field_label"
              label="Field Label"
              rules={[{ required: true, message: "Label is required" }]}
            >
              <Input placeholder="e.g. Customer Reference No." />
            </Form.Item>
            <Form.Item
              name="field_key"
              label="Field Key"
              rules={[
                { required: true, message: "Key is required" },
                {
                  pattern: /^[a-z0-9_]+$/,
                  message: "lowercase letters, numbers, underscores only",
                },
              ]}
            >
              <Input placeholder="e.g. customer_ref_no" disabled={!!editing} />
            </Form.Item>
          </div>

          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item
              name="entity_type"
              label="Entity Type"
              rules={[{ required: true, message: "Entity type is required" }]}
            >
              <Select
                placeholder="Select entity"
                options={ENTITY_TYPE_OPTIONS}
              />
            </Form.Item>
            <Form.Item
              name="field_type"
              label="Field Type"
              rules={[{ required: true, message: "Field type is required" }]}
            >
              <Select
                placeholder="Select type"
                options={FIELD_TYPE_OPTIONS}
                onChange={(v) => setFieldType(v as TCustomFieldType)}
              />
            </Form.Item>
          </div>

          <Form.Item
            name="company_id"
            label="Company"
            initialValue={lockedCompanyId ?? undefined}
            rules={[{ required: true, message: "Company is required" }]}
          >
            <Select
              showSearch
              placeholder="Select a company…"
              optionFilterProp="label"
              loading={companiesLoading}
              disabled={!!lockedCompanyId}
              options={companies.map((c) => ({
                value: c.id,
                label: `${c.name} — ${c.email}`,
              }))}
            />
          </Form.Item>

          {/* ── Options (for select / multiselect) ── */}
          {needsOptions && (
            <Form.Item label="Options">
              <div className="flex gap-2 mb-2">
                <Input
                  value={optionInput}
                  onChange={(e) => setOptionInput(e.target.value)}
                  onPressEnter={handleAddOption}
                  placeholder="Type an option and press Enter"
                />
                <Button onClick={handleAddOption}>Add</Button>
              </div>
              {options.length > 0 && (
                <div className="flex flex-wrap gap-1 p-2 border border-slate-200 rounded-lg bg-slate-50 min-h-[40px]">
                  {options.map((o) => (
                    <Tag key={o} closable onClose={() => handleRemoveOption(o)}>
                      {o}
                    </Tag>
                  ))}
                </div>
              )}
            </Form.Item>
          )}

          <Divider className="my-3" />

          <div className="grid grid-cols-3 gap-x-4">
            <Form.Item name="sort_order" label="Sort Order">
              <InputNumber style={{ width: "100%" }} min={0} />
            </Form.Item>
            <Form.Item
              name="is_required"
              label="Required"
              valuePropName="checked"
            >
              <Switch checkedChildren="Yes" unCheckedChildren="No" />
            </Form.Item>
            <Form.Item name="is_active" label="Active" valuePropName="checked">
              <Switch checkedChildren="Yes" unCheckedChildren="No" />
            </Form.Item>
          </div>

          <div className="flex justify-end gap-2 mt-2">
            <Button onClick={resetForm}>Cancel</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={creating || updating}
              style={{ background: "#4c1d95", borderColor: "#4c1d95" }}
            >
              {editing ? "Save Changes" : "Create Field"}
            </Button>
          </div>
        </Form>
      </Modal>

      {/* ── Detail Drawer ── */}
      <Drawer
        open={!!detail}
        onClose={() => setDetail(null)}
        title={detail?.field_label ?? "Field Details"}
        width={480}
      >
        {detail && (
          <div className="space-y-5">
            <div>
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-2">
                Field Definition
              </div>
              <Descriptions
                column={1}
                bordered
                size="small"
                labelStyle={{ fontWeight: 600, width: 140 }}
              >
                <Descriptions.Item label="ID">#{detail.id}</Descriptions.Item>
                <Descriptions.Item label="UUID">
                  <code className="text-xs">{detail.uuid}</code>
                </Descriptions.Item>
                <Descriptions.Item label="Label">
                  {detail.field_label}
                </Descriptions.Item>
                <Descriptions.Item label="Key">
                  <code className="text-xs">{detail.field_key}</code>
                </Descriptions.Item>
                <Descriptions.Item label="Entity Type">
                  <Tag color="indigo">{detail.entity_type}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Field Type">
                  <Tag
                    color={
                      TYPE_CFG_DETAIL[detail.field_type]?.color ?? "default"
                    }
                  >
                    {TYPE_CFG_DETAIL[detail.field_type]?.label ??
                      detail.field_type}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Required">
                  {detail.is_required ? "Yes" : "No"}
                </Descriptions.Item>
                <Descriptions.Item label="Active">
                  <Tag color={detail.is_active ? "green" : "default"}>
                    {detail.is_active ? "Active" : "Inactive"}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Sort Order">
                  {detail.sort_order}
                </Descriptions.Item>
                <Descriptions.Item label="Values Used">
                  {(detail as any).value_count ?? 0}
                </Descriptions.Item>
                <Descriptions.Item label="Created">
                  {dayjs(detail.created_at).format("DD MMM YYYY HH:mm")}
                </Descriptions.Item>
                <Descriptions.Item label="Updated">
                  {dayjs(detail.updated_at).format("DD MMM YYYY HH:mm")}
                </Descriptions.Item>
              </Descriptions>
            </div>
            {detail.options && (
              <div>
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-2">
                  Options
                </div>
                <div className="flex flex-wrap gap-1">
                  {(typeof detail.options === "string"
                    ? JSON.parse(detail.options)
                    : detail.options
                  ).map((o: string) => (
                    <Tag key={o}>{o}</Tag>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
}

// colour map used in detail drawer (same palette as table)
const TYPE_CFG_DETAIL: Record<string, { color: string; label: string }> = {
  text: { color: "blue", label: "Text" },
  number: { color: "purple", label: "Number" },
  date: { color: "cyan", label: "Date" },
  boolean: { color: "gold", label: "Boolean" },
  select: { color: "green", label: "Select" },
  multiselect: { color: "volcano", label: "Multi-select" },
  file: { color: "magenta", label: "File" },
};
